import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useMyThreads, useMyActiveTags, useMyAssignees, useAgenda, useTasksToValidate, useTeamMembers } from '../tareas/api';
import NotificationsBell from './NotificationsBell';

// Iconos de línea (trazo currentColor) — mismo lenguaje visual que las tarjetas.
const IC = {
  buscar: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6" /><path d="M16 16l-3.5-3.5" />
    </svg>
  ),
  bandeja: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 11h4l1.5 2.5h5L14 11h2" /><path d="M2 11l2.5-7h9L16 11v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" />
    </svg>
  ),
  menciones: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="9" r="3.2" /><path d="M12.5 9v1.2a2 2 0 0 0 4 0V9a7.5 7.5 0 1 0-3 6" />
    </svg>
  ),
  guardados: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z" />
    </svg>
  ),
  logout: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 14H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h3" /><path d="M10 11l3-3-3-3" /><path d="M13 8H6" />
    </svg>
  ),
  tag: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8.6 2H3a1 1 0 0 0-1 1v5.6a1 1 0 0 0 .3.7l5.4 5.4a1 1 0 0 0 1.4 0l4.6-4.6a1 1 0 0 0 0-1.4L8.7 2.3A1 1 0 0 0 8.6 2z" /><circle cx="5.5" cy="5.5" r="1" />
    </svg>
  ),
  settings: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  chevron: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  check: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  plus: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  invitar: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="8" r="3.2" /><path d="M3 20v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 1.6.27" /><path d="M17.5 12v6" /><path d="M14.5 15h6" />
    </svg>
  ),
  calendario: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="3.5" width="13" height="12" rx="2" /><path d="M2.5 7.5h13" /><path d="M6 2v3" /><path d="M12 2v3" />
    </svg>
  ),
  validar: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2.5h6a1 1 0 0 1 1 1V15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1z" /><path d="M7 2.2h4v2H7z" /><path d="M7 9.5l1.6 1.6L12 7.7" />
    </svg>
  ),
  siguiendo: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1.5 9s2.7-5 7.5-5 7.5 5 7.5 5-2.7 5-7.5 5-7.5-5-7.5-5z" /><circle cx="9" cy="9" r="2.2" />
    </svg>
  ),
};

export default function Sidebar() {
  const { profile, signOut, teams, activeTeamId, switchTeam } = useAuth();
  const { members } = useTeamMembers();
  // Badge de Bandeja = hilos en curso con novedades sin leer (vista "Todo").
  const { unreadCount: inboxUnread } = useMyThreads(profile?.id);
  const { assignees } = useMyAssignees(profile?.id);
  const { tags } = useMyActiveTags(profile?.id);
  const { counts: agenda } = useAgenda(profile?.id);
  const { tasks: toValidate } = useTasksToValidate(profile?.id);
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [wsOpen, setWsOpen] = useState(false);
  const wsRef = useRef<HTMLDivElement>(null);
  const activeTeam = teams.find((t) => t.id === activeTeamId) ?? null;

  useEffect(() => {
    if (!wsOpen) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wsRef.current && !wsRef.current.contains(e.target as Node)) setWsOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [wsOpen]);

  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [userMenuOpen]);

  // Detección de vista activa para resaltar también Team/Tags.
  const sp = new URLSearchParams(location.search);
  const inBandeja = location.pathname === '/bandeja';
  const activeAsignado = inBandeja ? sp.get('asignado') : null;
  const activeTag = inBandeja ? sp.get('tag') : null;
  const activeAgenda = location.pathname === '/agenda' ? (sp.get('vista') ?? '') : null;
  // "Bandeja" agrupa sus filtros (todo, menciones, siguiendo…): se resalta
  // en cualquiera de ellas, pero no en por_validar / tag / persona.
  const bandejaVista = inBandeja ? (sp.get('vista') ?? '') : null;
  const inBandejaTabs = inBandeja && !activeAsignado && !activeTag
    && (bandejaVista === '' || bandejaVista === 'menciones' || bandejaVista === 'siguiendo');

  return (
    <aside style={styles.aside}>
      <div style={styles.brandRow}>
        <div ref={wsRef} style={{ ...styles.brandLeft, position: 'relative' }}>
          <button type="button" onClick={() => setWsOpen((o) => !o)} style={styles.wsTrigger} title="Cambiar de espacio de trabajo">
            <TeamMark logoUrl={activeTeam?.logoUrl ?? null} nombre={activeTeam?.nombre ?? null} size={28} />
            <span style={styles.brand}>{activeTeam?.nombre ?? 'SimplyTeam'}</span>
            <span style={{ display: 'flex', color: '#94a3b8' }}>{IC.chevron}</span>
          </button>

          {wsOpen && (
            <div style={styles.wsMenu}>
              {/* Cabecera del equipo activo: marca, nombre y nº de miembros. */}
              <div style={styles.wsHeader}>
                <TeamMark logoUrl={activeTeam?.logoUrl ?? null} nombre={activeTeam?.nombre ?? null} size={34} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={styles.wsHeaderName}>{activeTeam?.nombre ?? 'SimplyTeam'}</div>
                  <div style={styles.wsHeaderMeta}>
                    {members.length > 0 ? `${members.length} miembro${members.length === 1 ? '' : 's'}` : 'Tu equipo'}
                  </div>
                </div>
              </div>

              {/* Invitar es una acción del ESPACIO (como en Notion/Twist), no de
                  la cuenta personal: por eso vive aquí y no en el menú de usuario. */}
              <button
                type="button"
                onClick={() => { setWsOpen(false); navigate('/perfil?seccion=invitaciones'); }}
                style={styles.wsInvite}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#eff5ff'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ display: 'flex' }}>{IC.invitar}</span>
                <span>Invitar a miembros</span>
              </button>

              <div style={styles.wsDivider} />

              <div style={styles.wsMenuLabel}>Espacios de trabajo</div>
              {teams.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { setWsOpen(false); if (t.id !== activeTeamId) { switchTeam(t.id); navigate('/bandeja'); } }}
                  style={styles.wsItem}
                >
                  <TeamMark logoUrl={t.logoUrl} nombre={t.nombre} size={22} />
                  <span style={{ flex: 1, fontWeight: t.id === activeTeamId ? 700 : 500, color: '#0f172a' }}>{t.nombre}</span>
                  {t.id === activeTeamId && <span style={{ display: 'flex', color: '#2563eb' }}>{IC.check}</span>}
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setWsOpen(false); navigate('/crear-espacio'); }}
                style={{ ...styles.wsItem, borderTop: '1px solid #f1f5f9', color: '#1d4ed8', fontWeight: 600 }}
              >
                <span style={{ display: 'flex' }}>{IC.plus}</span>
                <span>Crear espacio de trabajo</span>
              </button>
            </div>
          )}
        </div>
        <NotificationsBell />
      </div>

      <div style={styles.scroll}>
      <Group title="Workspace">
        <Item to="/buscar" icon={IC.buscar} label="Buscar" />
        <Item to="/bandeja" icon={IC.bandeja} label="Bandeja" badge={inboxUnread || null} active={inBandejaTabs} />
        <Item to="/bandeja?vista=por_validar" icon={IC.validar} label="Por validar" badge={toValidate.length || null} />
        <Item to="/agenda?vista=calendario" icon={IC.calendario} label="Calendario" />
        <Item to="/guardados" icon={IC.guardados} label="Guardados" />
      </Group>

      <Group title="Team" collapsible count={assignees.reduce((s, a) => s + (a.openCount || 0), 0)}>
        {/* Solo personas con hilos en curso asignados: quita ruido de quienes
            ya no tienen nada pendiente. */}
        {assignees.filter((a) => a.openCount > 0).map(({ profile: p, openCount }) => (
          <RowButton
            key={p.id}
            dot={p.color}
            label={p.nombre}
            labelColor={p.color}
            badge={openCount || null}
            active={activeAsignado === p.id}
            onClick={() => navigate(`/bandeja?asignado=${p.id}`)}
          />
        ))}
        {assignees.filter((a) => a.openCount > 0).length === 0 && (
          <div style={styles.empty}>Aquí aparecerán las personas a las que asignes hilos.</div>
        )}
      </Group>

      <Group title="Tags" collapsible count={tags.reduce((s, t) => s + (t.tasksCount || 0), 0)}>
        {tags.map((t) => (
          <RowButton
            key={t.id}
            dot={t.color}
            icon={IC.tag}
            label={t.nombre}
            labelColor={t.color}
            badge={t.tasksCount || null}
            active={activeTag === t.id}
            onClick={() => navigate(`/bandeja?tag=${t.id}`)}
          />
        ))}
        {tags.length === 0 && (
          <div style={styles.empty}>Aquí aparecerán las tags que apliques a hilos.</div>
        )}
      </Group>

      <Group title="Agenda" collapsible count={(agenda.vencidas || 0) + (agenda.hoy || 0) + (agenda.proximas || 0)}>
        <RowButton
          dot="#dc2626"
          label="Vencidas"
          labelColor="#0f172a"
          badge={agenda.vencidas || null}
          active={activeAgenda === 'vencidas'}
          onClick={() => navigate('/agenda?vista=vencidas')}
        />
        <RowButton
          dot="#2563eb"
          label="Hoy"
          labelColor="#0f172a"
          badge={agenda.hoy || null}
          active={activeAgenda === 'hoy'}
          onClick={() => navigate('/agenda?vista=hoy')}
        />
        <RowButton
          dot="#94a3b8"
          label="Próximas"
          labelColor="#0f172a"
          badge={agenda.proximas || null}
          active={activeAgenda === 'proximas'}
          onClick={() => navigate('/agenda?vista=proximas')}
        />
        <RowButton
          dot="#16a34a"
          label="Completados"
          labelColor="#0f172a"
          active={activeAgenda === 'completadas'}
          onClick={() => navigate('/agenda?vista=completadas')}
        />
      </Group>
      </div>

      <div ref={userMenuRef} style={styles.user}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
          <button
            type="button"
            onClick={() => setUserMenuOpen((o) => !o)}
            style={styles.userTrigger}
          >
            <span style={{ ...styles.avatar, background: profile?.color || '#94a3b8' }}>
              {(profile?.nombre || '?').charAt(0).toUpperCase()}
            </span>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {profile?.nombre || '—'}
              </div>
            </div>
          </button>
          <button onClick={signOut} title="Salir" style={styles.signoutBtn}>{IC.logout}</button>

          {userMenuOpen && (
            <div style={styles.userMenu}>
              <button
                type="button"
                onClick={() => { setUserMenuOpen(false); navigate('/perfil'); }}
                style={styles.userMenuItem}
              >
                <span style={styles.menuIcon}>{IC.settings}</span>
                <span>Configuración</span>
              </button>
              <button
                type="button"
                onClick={() => { setUserMenuOpen(false); signOut(); }}
                style={styles.userMenuItem}
              >
                <span style={styles.menuIcon}>{IC.logout}</span>
                <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

// Marca del equipo: logo subido si existe; si no, monograma con la inicial;
// y como último recurso (sin equipo) la marca de SimplyTeam.
function TeamMark({ logoUrl, nombre, size }: { logoUrl: string | null; nombre: string | null; size: number }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt=""
        width={size}
        height={size}
        style={{ borderRadius: 6, objectFit: 'cover', flexShrink: 0, display: 'block' }}
      />
    );
  }
  if (nombre) {
    return (
      <span
        style={{
          width: size, height: size, minWidth: size, borderRadius: 6,
          background: '#eef2ff', color: '#4338ca', fontWeight: 800,
          fontSize: size * 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {nombre.charAt(0).toUpperCase()}
      </span>
    );
  }
  return <img src={`${process.env.PUBLIC_URL}/simplyteam-mark.svg`} alt="" width={size} height={size} />;
}

interface GroupProps {
  title: string;
  children: ReactNode;
  // Si es plegable, la cabecera actúa como botón y el estado se recuerda
  // en localStorage por título (sobrevive a recargas y navegación).
  collapsible?: boolean;
  // Número que se muestra junto al título cuando el grupo está plegado,
  // para no perder la señal de cuánto hay dentro.
  count?: number;
}

function Group({ title, children, collapsible = false, count = 0 }: GroupProps) {
  const storageKey = `bktask:sidebar:group:${title.toLowerCase()}`;
  const [open, setOpen] = useState(() => {
    if (!collapsible) return true;
    try { return localStorage.getItem(storageKey) !== 'closed'; } catch { return true; }
  });

  if (!collapsible) {
    return (
      <div style={styles.group}>
        <div style={styles.groupTitle}>{title}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</div>
      </div>
    );
  }

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      try { localStorage.setItem(storageKey, next ? 'open' : 'closed'); } catch { /* noop */ }
      return next;
    });
  };

  return (
    <div style={styles.group}>
      <button
        type="button"
        onClick={toggle}
        style={styles.groupTitleBtn}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#334155'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = '#64748b'; }}
      >
        <span style={{ ...styles.groupChevron, transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}>{IC.chevron}</span>
        <span style={{ flex: 1 }}>{title}</span>
        {!open && count > 0 && <span style={styles.groupCount}>{count}</span>}
      </button>
      {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{children}</div>}
    </div>
  );
}

interface ItemProps {
  to: string;
  icon: ReactNode;
  label: string;
  badge?: number | null;
  active?: boolean;
}

// Active = pathname match AND query string match exacto. Distingue
// /bandeja (sin search) de /bandeja?vista=menciones sin que ambos resalten.
// `active` permite forzarlo (p. ej. Bandeja se resalta en todas sus pestañas).
function Item({ to, icon, label, badge, active }: ItemProps) {
  const location = useLocation();
  const [path, search = ''] = to.split('?');
  const isActive = active ?? (location.pathname === path && (location.search.replace(/^\?/, '') === search));

  return (
    <Link
      to={to}
      style={{ ...styles.item, ...(isActive ? styles.itemActive : null) }}
      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f8fafc'; }}
      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{ ...styles.iconWrap, color: isActive ? '#1d4ed8' : '#64748b' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
      {badge ? <span style={styles.badge}>{badge}</span> : null}
    </Link>
  );
}

interface RowButtonProps {
  dot: string;
  label: string;
  labelColor: string;
  badge?: number | null;
  active?: boolean;
  onClick: () => void;
  // Si se pasa, sustituye al punto de color (tintado con `dot`). P. ej. las
  // tags usan el icono universal de etiqueta en vez del punto.
  icon?: ReactNode;
}

// Ítem de Team/Tags: punto (o icono) en su color + nombre en su color + contador.
function RowButton({ dot, label, labelColor, badge, active = false, onClick, icon }: RowButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...styles.teamItem, ...(active ? styles.itemActive : null) }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = '#f8fafc'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      {icon
        ? <span style={{ ...styles.rowIcon, color: dot }}>{icon}</span>
        : <span style={{ ...styles.dot, background: dot }} />}
      <span style={{ flex: 1, textAlign: 'left', color: labelColor, fontWeight: 600 }}>{label}</span>
      {badge ? <span style={styles.badge}>{badge}</span> : null}
    </button>
  );
}

const styles: Record<string, CSSProperties> = {
  aside: {
    width: 268,
    minWidth: 268,
    height: '100vh',
    background: '#fff',
    borderRight: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 14px 14px',
    fontFamily: "'Söhne', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  // Zona central scrollable: cabecera (marca) y pie (usuario) quedan fijos,
  // y solo los grupos hacen scroll cuando crecen Team/Tags.
  scroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    paddingTop: 4,
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
    gap: 8,
    marginBottom: 14,
  },
  brandLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  brand: {
    fontSize: 18,
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: -0.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  wsTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    background: 'transparent',
    border: 'none',
    padding: '4px 6px',
    borderRadius: 8,
    cursor: 'pointer',
  },
  wsMenu: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    minWidth: 230,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 12px 28px rgba(15,23,42,0.16)',
    overflow: 'hidden',
    zIndex: 200,
    padding: 4,
  },
  wsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px 10px',
  },
  wsHeaderName: {
    fontSize: 14.5,
    fontWeight: 800,
    color: '#0f172a',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  wsHeaderMeta: {
    fontSize: 12,
    color: '#94a3b8',
    marginTop: 1,
  },
  wsInvite: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '9px 10px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    color: '#1d4ed8',
    textAlign: 'left',
    borderRadius: 8,
    transition: 'background .12s ease',
  },
  wsDivider: {
    height: 1,
    background: '#f1f5f9',
    margin: '4px 6px',
  },
  wsMenuLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    padding: '8px 10px 4px',
  },
  wsItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 10px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    color: '#0f172a',
    textAlign: 'left',
    borderRadius: 8,
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  groupTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    letterSpacing: 1,
    textTransform: 'uppercase',
    padding: '0 8px',
  },
  groupTitleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    width: '100%',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    letterSpacing: 1,
    textTransform: 'uppercase',
    padding: '2px 4px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'color .12s ease',
  },
  groupChevron: {
    display: 'flex',
    alignItems: 'center',
    transition: 'transform .15s ease',
    transformOrigin: 'center',
  },
  groupCount: {
    background: '#f1f5f9',
    color: '#64748b',
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0,
    padding: '1px 7px',
    borderRadius: 999,
    minWidth: 16,
    textAlign: 'center',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    textDecoration: 'none',
    color: '#334155',
    padding: '8px 10px',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    transition: 'background .12s ease',
  },
  itemActive: {
    background: '#eff5ff',
    color: '#1d4ed8',
    fontWeight: 600,
  },
  teamItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'transparent',
    border: 'none',
    padding: '8px 10px',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 14,
    transition: 'background .12s ease',
  },
  iconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
  },
  dot: {
    width: 8,
    height: 8,
    minWidth: 8,
    borderRadius: '50%',
  },
  // Icono en lugar del punto (p. ej. tags). Mismo hueco para no desalinear.
  rowIcon: {
    width: 16,
    minWidth: 16,
    height: 16,
    marginLeft: -4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    background: '#eff5ff',
    color: '#2563eb',
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 999,
    minWidth: 18,
    textAlign: 'center',
  },
  empty: {
    fontSize: 12,
    color: '#94a3b8',
    padding: '4px 10px',
    lineHeight: 1.4,
  },
  user: {
    borderTop: '1px solid #e2e8f0',
    padding: '12px 4px 0',
    marginTop: 4,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    color: '#fff',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
  },
  signoutBtn: {
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '6px 8px',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
  },
  userTrigger: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  userMenu: {
    position: 'absolute',
    bottom: 'calc(100% + 8px)',
    left: 0,
    minWidth: 200,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
    overflow: 'hidden',
    zIndex: 200,
  },
  userMenuItem: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    color: '#0f172a',
    textAlign: 'left',
  },
  menuIcon: {
    display: 'flex',
    alignItems: 'center',
    color: '#64748b',
  },
};
